import { ColumnInfo, ColumnInfoList, DisplaySetting } from 'components/shared/display_setting'
import { TableCell } from 'components/shared/widget'
import { RequestFormIssuance } from 'entities/request_form_issuance'
import { ReactNode, useMemo } from 'react'
import { Form } from 'utils/hooks'
import { RequestFormIssuanceViewModel } from 'view_models/request_form_issuance_view_model'
import { Badge, OverlayTrigger, Popover, Table } from 'react-bootstrap'
import { RequestFormViewModel } from 'view_models/request_form_view_model'
import { SampleViewModel } from 'view_models/sample_view_model'

export const requestFormIssuanceColumnInfoList = (form?: Form<Record<string, unknown>>): ColumnInfoList => {
    const isHomeTest = process.env.REACT_APP_IS_HOME_TEST
    const values: ColumnInfo[] = [
        { name: 'ID', th: true, width: 50, form: form, orderAttr: 'requestFormIssuance_id', searchType: { name: 'id', type: 'multiple_text' } },
        {
            name: '発行日',
            th: true,
            width: 100,
            form: form,
            orderAttr: 'request_form_issuance_created_at',
            searchType: { name: 'created_at', type: 'date' },
        },
        { name: '発行数', th: true, width: 100, align: 'center' },
        { name: 'プロダクト', th: true, width: 100, align: 'center' },
        {
            name: 'ステータス',
            align: 'center',
            th: true,
            width: 100,
            form: form,
            orderAttr: 'requestFormIssuance_collected',
            searchType: { name: 'collected', type: 'boolean' },
        },
        {
            name: '印刷日時',
            th: true,
            width: 150,
            form: form,
            orderAttr: 'request_form_issuance_printed_at',
            searchType: { name: 'printed_at', type: 'date' },
        },
        {
            name: '回収日時',
            th: true,
            width: 150,
            form: form,
            orderAttr: 'request_form_issuance_collected_at',
            searchType: { name: 'collected_at', type: 'date' },
        },
    ]

    if (isHomeTest) {
        values.push({ name: '送付済', th: true, minWidth: 200 })
    }
    values.push({ name: '回収済', th: true, minWidth: 200 })
    return {
        tableName: '',
        values: values,
    }
}

export type RequestFormIssuanceTableDataProps = {
    requestFormIssuance?: RequestFormIssuance
    displaySetting: DisplaySetting
}

export const RequestFormIssuanceTableData = ({ requestFormIssuance, displaySetting }: RequestFormIssuanceTableDataProps): JSX.Element => {
    const isHomeTest = process.env.REACT_APP_IS_HOME_TEST
    if (!requestFormIssuance) {
        return (
            <>
                {displaySetting.columnInfoList.values.map((_: ColumnInfo, index: number) => {
                    if (displaySetting.hiddenList.includes(index)) {
                        return <></>
                    } else {
                        return <TableCell key={index}></TableCell>
                    }
                })}
            </>
        )
    }
    const vm = new RequestFormIssuanceViewModel(requestFormIssuance)

    const createCells = useMemo(() => {
        return (
            <>
                <TableCell>{requestFormIssuance.id}</TableCell>
                <TableCell>{vm.createDate()}</TableCell>
                <TableCell align="center">
                    <OverlayTrigger
                        rootClose
                        trigger="click"
                        placement="bottom-start"
                        overlay={
                            <Popover id={`popover-request_forms-${requestFormIssuance.id}`} className="shadow">
                                <Popover.Body>
                                    <div style={{ width: 700, maxHeight: 500, overflow: 'scroll' }}>
                                        <Table style={{ textAlign: 'center' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ width: 140 }}>依頼書コード</th>
                                                    <th style={{ width: 140 }}>検体コード</th>
                                                    <th style={{ width: 100 }}>状況</th>
                                                    <th style={{ width: 100 }}>検品状況</th>
                                                    <th style={{ width: 150 }}>回収日時</th>
                                                    <th style={{ width: 100 }}>搬送報告ID</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {requestFormIssuance.requestForms?.map((requestForm) => {
                                                    let sampleVm: undefined | SampleViewModel = undefined
                                                    if (requestForm.sample) {
                                                        sampleVm = new SampleViewModel(requestForm.sample)
                                                    }
                                                    return (
                                                        <tr key={'request-form-' + requestForm.id}>
                                                            <td>{requestForm.code}</td>
                                                            <td>{requestForm.sample ? requestForm.sample.code : '-'}</td>
                                                            <td>
                                                                {sampleVm ? (
                                                                    sampleVm.statusView()
                                                                ) : requestForm.isOld ? (
                                                                    <div style={{ fontSize: 12, whiteSpace: "nowrap" }}>(再採尿前の依頼書)</div>
                                                                ) : (
                                                                    <div style={{ fontSize: 12 }}>未回収</div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {sampleVm ? (
                                                                    sampleVm.inspectionStatusView()
                                                                ) : requestForm.isOld ? (
                                                                    <Badge bg={'success'} style={{ width: 80 }}>
                                                                        {'確認済み'}
                                                                    </Badge>
                                                                ) : (
                                                                    <div style={{ fontSize: 12 }}>未回収</div>
                                                                )}
                                                            </td>
                                                            <td>{new RequestFormViewModel(requestForm).collectedAtText()}</td>
                                                            <td>{sampleVm ? Number(sampleVm.object.transportationReport) : ''}</td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Popover.Body>
                            </Popover>
                        }
                    >
                        <div className="linked-text">{requestFormIssuance.requestForms?.length} 枚</div>
                    </OverlayTrigger>
                </TableCell>
                <TableCell align="center">{requestFormIssuance.targetProduct}</TableCell>
                <TableCell align="center">{vm.statusView()}</TableCell>
                <TableCell>{vm.printedAtText()}</TableCell>
                <TableCell>{vm.collectedAtText()}</TableCell>
                {isHomeTest && <TableCell>{vm.sentRateView()}</TableCell>}
                <TableCell>{vm.collectedRateView()}</TableCell>
            </>
        )
    }, [displaySetting.hiddenList])

    return createCells.props.children.filter((_: ReactNode, index: number) => !displaySetting.hiddenList?.includes(index)).map((cell: ReactNode) => cell)
}
